/* eslint-disable no-console */
import { Injectable } from '@angular/core';
import { CentralDatabaseDataImporterHandler } from './central-database';
import {
	ConfigDatabaseDataImporterHandler,
	ConfigSchemaGeneratorHandler,
	ConfigDatabaseRefTableHandler
} from './config-workspace-database';
import { WorkspaceSchemaGeneratorHandler, WorkspaceDataImportHandler } from './data-workspace-database';
import { FileModel, ModalService, ToastService } from '@shared';
import {
	JsonFileDataParser,
	TableInfoRepository,
	WorkspaceIdGeneratorService,
	WorkspaceRepository
} from '@database/central';
import {
	WorkspaceDatabaseProvider,
	ColumnRepository,
	TableRepository,
	WorkspaceDataImporterService,
	WorkspaceSchemaGeneratorService,
	RefTablesStorageService,
	DeviceIdProvider
} from '@database/workspace';
import { FileSanitizerHandler } from './file-sanitizer';
import { FileSanitizerService, EditWorkFileNameHandler, RegistrationValidateTokenService } from '../services';
import { LoggingService } from '@shared/logging';
import { TranslateService } from '@ngx-translate/core';
import { ReservedRxdbKeywordsSanitizerHandler } from './file-sanitizer/reserved-rxdb-keywords-sanitizer-handler';
import { FileValidatorHandler } from './validation';
import { RegistrationHandler, RegistrationValidatorHandler } from './registration';
import { DecodeJWTService } from '@domain/registration';
import { AuthProvider } from '@domain/authentication';
import { AuthenticationHandler } from './authentication';
import { DataAndStructureValidator } from './validation/data-and-structure-validator-handler';

@Injectable({
	providedIn: 'root'
})
export class ImportOrchestratorService {
	private isImportedFromFile = true;

	public constructor(
		private workspaceSchemaGeneratorService: WorkspaceSchemaGeneratorService,
		private workspaceDataImporterService: WorkspaceDataImporterService,
		private jsonFileDataParser: JsonFileDataParser,
		private workspaceRepository: WorkspaceRepository,
		private workspaceDatabaseProvider: WorkspaceDatabaseProvider,
		private columnRepository: ColumnRepository,
		private tableRepository: TableRepository,
		private workspaceGeneratorService: WorkspaceIdGeneratorService,
		private modalService: ModalService,
		private fileSanitizerService: FileSanitizerService,
		private loggingService: LoggingService,
		private tableInfoRepository: TableInfoRepository,
		private translateService: TranslateService,
		private refTablesStorageService: RefTablesStorageService,
		private deviceIdProvider: DeviceIdProvider,
		private decodeJWTService: DecodeJWTService,
		private registrationValidateTokenService: RegistrationValidateTokenService,
		private toastService: ToastService,
		private authProvider: AuthProvider
	) {}

	public async startProcess(model: FileModel) {
		// 1. user access handler
		const userAccessHandler = new AuthenticationHandler(
			this.loggingService,
			this.toastService,
			this.translateService,
			this.authProvider
		);

		// 2. registration handler
		const registrationHandler = new RegistrationHandler(this.loggingService, this.deviceIdProvider);

		// 3. registration handler
		const registrationValidator = new RegistrationValidatorHandler(
			this.loggingService,
			this.translateService,
			this.deviceIdProvider,
			this.decodeJWTService,
			this.registrationValidateTokenService,
			this.toastService
		);

		// 4. validate file handler
		const fileValidator = new FileValidatorHandler(
			this.loggingService,
			this.jsonFileDataParser,
			this.toastService,
			this.translateService
		);

		// 5. Validate data and structure, making sure that tables in TABLE_DATA are present in FusionDataTables
		const dataAndStructureValidator = new DataAndStructureValidator(this.toastService, this.translateService);

		// 6. Sanitizer the file content (make keys and some key's values lowerCase(), ...)
		const fileSanitizer = new FileSanitizerHandler(this.fileSanitizerService, this.loggingService);

		// 7. Rename reserved rxdb words
		const reservedWordsSanitizer = new ReservedRxdbKeywordsSanitizerHandler(this.loggingService);

		// 8. Edit Workfile name
		const workFileNameEditor = new EditWorkFileNameHandler(this.modalService, this.loggingService);

		// 9. add file information in central database along with projects (get projects list from refference tables)
		const centralDatabaseDataImporter = new CentralDatabaseDataImporterHandler(
			this.jsonFileDataParser,
			this.workspaceRepository,
			this.workspaceGeneratorService,
			this.loggingService,
			this.isImportedFromFile
		);

		// 10. create workspace database for project (table schemas are predefined)
		const configDatabaseSchemaGenerator = new ConfigSchemaGeneratorHandler(
			this.workspaceDatabaseProvider,
			this.loggingService,
			this.workspaceRepository
		);

		// 11. insert metadata info (tables, columns, refferencetables, users)
		// note - reference tables will be kept as string in attachements
		const configDatabaseDataImporter = new ConfigDatabaseDataImporterHandler(
			this.columnRepository,
			this.tableRepository,
			this.loggingService,
			this.toastService,
			this.translateService,
			this.tableInfoRepository,
			this.workspaceRepository
		);

		// 12. create ref tables
		const refTablesHandler = new ConfigDatabaseRefTableHandler(
			this.jsonFileDataParser,
			this.loggingService,
			this.tableInfoRepository,
			this.workspaceRepository,
			this.refTablesStorageService
		);

		// 13.  schema is created dynamically based on the tables and columns from metadata
		const workspaceSchemaGenerator = new WorkspaceSchemaGeneratorHandler(
			this.workspaceSchemaGeneratorService,
			this.loggingService,
			this.workspaceRepository
		);

		// 14. insert data into the database created at 6 and with schemas prepared at 8
		const workspaceDataImporter = new WorkspaceDataImportHandler(
			this.workspaceDataImporterService,
			this.loggingService,
			this.workspaceRepository
		);

		userAccessHandler.setNext(registrationHandler);
		registrationHandler.setNext(registrationValidator);
		registrationValidator.setNext(fileValidator);
		fileValidator.setNext(fileSanitizer);
		fileSanitizer.setNext(dataAndStructureValidator);
		dataAndStructureValidator.setNext(reservedWordsSanitizer);
		reservedWordsSanitizer.setNext(workFileNameEditor);
		workFileNameEditor.setNext(centralDatabaseDataImporter);
		centralDatabaseDataImporter.setNext(configDatabaseSchemaGenerator);
		configDatabaseSchemaGenerator.setNext(refTablesHandler);
		refTablesHandler.setNext(configDatabaseDataImporter);
		configDatabaseDataImporter.setNext(workspaceSchemaGenerator);
		workspaceSchemaGenerator.setNext(workspaceDataImporter);

		try {
			await userAccessHandler.handle(model);
		} catch (error) {
			if (model.workFileId) {
				await centralDatabaseDataImporter.cleanup(model);
			}

			throw error;
		}
	}
}
